/**
 * Utilitários para cálculos de parcelas, juros e descontos
 */

/**
 * Valida se um valor é numérico e positivo
 * @param {number} valor - Valor a ser validado
 * @param {string} nomeCampo - Nome do campo para mensagem de erro
 * @returns {boolean} - true se o valor for válido
 */
export const validarValorNumerico = (valor, nomeCampo) => {
  if (typeof valor !== 'number' && isNaN(parseFloat(valor))) {
    console.error(`Valor inválido para ${nomeCampo}:`, valor);
    return false;
  }
  return true;
};

/**
 * Calcula os dias de atraso de uma parcela
 * @param {Object} parcela - Objeto da parcela
 * @returns {number} - Número de dias de atraso
 */
export const calcularDiasAtraso = (parcela) => {
  if (!parcela || !parcela.data_vencimento) return 0;
  
  const vencimento = new Date(parcela.data_vencimento);
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  
  const umDia = 24 * 60 * 60 * 1000;
  const diferencaDias = Math.floor((hoje - vencimento) / umDia);
  return Math.max(1, diferencaDias);
};

/**
 * Calcula juros manuais baseados em percentual ou valor fixo
 */
export const calcularJurosManuais = (valorOriginal, tipoJuros, jurosPercentual, valorJurosFixo) => {
  console.log('[calculosUtils] calcularJurosManuais: Entradas ->', { valorOriginal, tipoJuros, jurosPercentual, valorJurosFixo });
  if (!validarValorNumerico(valorOriginal, 'valorOriginal')) return 0;
  
  let jurosManuais = 0;
  
  if (tipoJuros === 'percentual' && (jurosPercentual || jurosPercentual === 0)) {
    const taxa = Math.max(0, parseFloat(jurosPercentual) || 0);
    jurosManuais = valorOriginal * (taxa / 100);
    console.log('[calculosUtils] calcularJurosManuais: Cálculo percentual -> Taxa:', taxa, 'Juros:', jurosManuais);
  } else if (tipoJuros === 'fixo' && (valorJurosFixo || valorJurosFixo === 0)) {
    jurosManuais = Math.max(0, parseFloat(valorJurosFixo) || 0);
    console.log('[calculosUtils] calcularJurosManuais: Cálculo fixo -> Juros:', jurosManuais);
  }
  console.log('[calculosUtils] calcularJurosManuais: Retorno ->', jurosManuais);
  return jurosManuais;
};

/**
 * Calcula desconto baseado em percentual ou valor fixo
 */
export const calcularDesconto = (valorOriginal, tipoDesconto, descontoPercentual, valorDescontoFixo) => {
  if (!validarValorNumerico(valorOriginal, 'valorOriginal')) return 0;
  
  let desconto = 0;
  
  if (tipoDesconto === 'percentual' && (descontoPercentual || descontoPercentual === 0)) {
    const percentual = Math.min(100, Math.max(0, parseFloat(descontoPercentual) || 0));
    desconto = valorOriginal * (percentual / 100);
  } else if (tipoDesconto === 'fixo' && (valorDescontoFixo || valorDescontoFixo === 0)) {
    desconto = Math.min(valorOriginal, Math.max(0, parseFloat(valorDescontoFixo) || 0));
  }
  
  return desconto;
};

/**
 * Processa uma parcela individual, aplicando juros e descontos conforme necessário
 */
export const processarParcelaIndividual = ({
  parcela,
  tipoJuros,
  jurosPercentual,
  valorJurosFixo,
  tipoDesconto,
  descontoPercentual,
  valorDescontoFixo,
  aplicarJurosAutomaticos,
  valorManual = null
}) => {
  try {
    const valorOriginal = Math.max(0, parseFloat(parcela.valor_original || parcela.valor) || 0);
    
    // Remover cálculo local de juros automáticos
    // const jurosAutomaticosCalculado = aplicarJurosAutomaticos ? calcularJurosAutomaticos(valorOriginal) : 0;
    const jurosAutomaticosCalculado = 0; // Juros automáticos devem vir do backend
    
    // Calcular juros manuais sobre o valor original
    const jurosManuaisCalculado = calcularJurosManuais(valorOriginal, tipoJuros, jurosPercentual, valorJurosFixo);
    
    // Valor total antes do desconto
    const valorAntesDesconto = valorOriginal + jurosAutomaticosCalculado + jurosManuaisCalculado;
    
    // Calcular desconto sobre o valor total
    const descontoCalculado = calcularDesconto(valorAntesDesconto, tipoDesconto, descontoPercentual, valorDescontoFixo);
    
    // Calcular valor final
    let valorCalculado = valorAntesDesconto - descontoCalculado;
    valorCalculado = Math.max(0, Math.round(valorCalculado * 100) / 100);
    
    // Determinar percentuais de juros/desconto
    const jurosManuaisPercentualCalculado = valorOriginal > 0 ? (jurosManuaisCalculado / valorOriginal) * 100 : 0;
    const jurosAutomaticosPercentualCalculado = 0; // Juros automáticos devem vir do backend
    const descontoPercentualCalculado = valorOriginal > 0 ? (descontoCalculado / valorOriginal) * 100 : 0;

    console.log(`[calculosUtils] DEBUG: Final valorCalculado para parcela ${parcela.id}:`, valorCalculado);

    return { 
      id: parcela.id, 
      valorFinal: valorCalculado,
      detalhes: {
        valorOriginal,
        jurosAutomaticos: jurosAutomaticosCalculado,
        jurosManuais: jurosManuaisCalculado,
        desconto: descontoCalculado,
        valorFinal: valorCalculado,
        jurosManuaisPercentual: jurosManuaisPercentualCalculado,
        jurosAutomaticosPercentual: jurosAutomaticosPercentualCalculado,
        descontoPercentual: descontoPercentualCalculado,
      }
    };
  } catch (error) {
    console.error(`Erro ao processar parcela ${parcela.id || 'sem ID'}:`, error);
    return { 
      id: parcela.id, 
      valorFinal: 0,
      erro: error.message,
      detalhes: {
        valorOriginal: 0,
        jurosManuais: 0,
        jurosAutomaticos: 0,
        desconto: 0,
        valorFinal: 0,
        jurosManuaisPercentual: 0,
        jurosAutomaticosPercentual: 0,
        descontoPercentual: 0,
        erro: error.message
      }
    };
  }
};

/**
 * Verifica se um valor foi modificado manualmente pelo usuário
 */
export const foiModificadoManualmente = (valorAtual, valorOriginal, tolerancia = 0.01) => {
  if (isNaN(parseFloat(valorAtual)) || isNaN(parseFloat(valorOriginal))) {
    return false;
  }
  return Math.abs(parseFloat(valorAtual) - parseFloat(valorOriginal)) > tolerancia;
};
